import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Fade,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore,
  ExpandLess,
  MoreVert,
  Edit,
  Delete,
  Save,
  Close,
  DeleteOutline,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import moment from "moment";

// Utility functions
const getName = (msg) =>
  msg.createdBy?.personName || msg.senderName || msg.author?.name || "User";
const getInitials = (msg) => {
  const name = getName(msg);
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();
};
const getContent = (msg) => msg.message || msg.content || "";
const getTime = (msg) =>
  msg.createdOn ? moment(msg.createdOn).format("hh:mm A") : "";
const getRelativeTime = (msg) =>
  msg.createdOn ? moment(msg.createdOn).fromNow() : "";

// Check if message can be edited/deleted (within 5 minutes)
const canEditOrDelete = (msg) => {
  if (!msg.createdOn) return false;
  const messageTime = moment(msg.createdOn);
  const now = moment();
  const diffInMinutes = now.diff(messageTime, "minutes");
  return diffInMinutes <= 5;
};

// Component to render attachment with simple text
const ImageAttachment = ({ url, isRightSide }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isRightSide ? "flex-end" : "flex-start",
      }}
    >
      <Box
        sx={{
          display: "inline-block",
          padding: "8px 12px",
          borderRadius: "8px",
          bgcolor: "#f3f4f6",
          border: "1px solid #e5e7eb",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            bgcolor: "#e5e7eb",
            transform: "scale(1.02)",
          },
        }}
        onClick={() => window.open(url, "_blank")}
      >
        <Typography
          variant="body2"
          sx={{
            color: "#3b82f6",
            fontSize: "14px",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          📎 Attachment
        </Typography>
      </Box>
    </Box>
  );
};

// MessageContainer Component
const MessageContainer = ({
  msg,
  idx,
  isExpanded,
  onExpand,
  currentUser = null,
  onEdit,
  onDelete,
  isEditing: isEditingExternal = false,
  isDeleting: isDeletingExternal = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState(getContent(msg));
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const content = getContent(msg);
  const shouldTruncate = content.length > 200;
  const canModify = canEditOrDelete(msg);

  // Check if this specific message is being edited or deleted
  const messageId = msg._id || msg.id;
  const isMessageEditing = isEditingExternal && isEditingExternal === messageId;
  const isMessageDeleting =
    isDeletingExternal && isDeletingExternal === messageId;

  // Determine if message should be on the right side
  const isRightSide =
    msg.direction === "school_to_student" && currentUser?.role !== "student";

  // Define colors based on message side
  const messageColors = isRightSide
    ? {
        bgcolor: "#e3f2fd", // Light blue for right side (school messages)
        borderColor: "#bbdefb",
        avatarBg: "#1976d2",
        avatarColor: "#fff",
      }
    : {
        bgcolor: "#f3e5f5", // Light purple for left side (student/other messages)
        borderColor: "#e1bee7",
        avatarBg: "#7b1fa2",
        avatarColor: "#fff",
      };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditStart = () => {
    setIsEditing(true);
    setEditedContent(content);
    handleMenuClose();
  };

  const handleEditSave = () => {
    if (editedContent.trim() && editedContent !== content) {
      onEdit?.(msg._id || msg.id, editedContent.trim());
    }
    setIsEditing(false);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setEditedContent(content);
  };

  const handleDeleteConfirm = () => {
    onDelete?.(msg._id || msg.id);
    setDeleteDialogOpen(false);
    handleMenuClose();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isRightSide ? "flex-end" : "flex-start",
        mb: 3,
        px: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: isRightSide ? "row-reverse" : "row",
          alignItems: "flex-start",
          maxWidth: "75%",
          gap: 1.5,
        }}
      >
        {/* Avatar positioned outside the message bubble */}
        <Avatar
          sx={{
            bgcolor: messageColors.avatarBg,
            color: messageColors.avatarColor,
            fontWeight: 700,
            width: 36,
            height: 36,
            fontSize: "14px",
            mt: 0.5, // Slight offset to align with message content
          }}
        >
          {getInitials(msg)}
        </Avatar>

        {/* Message content container */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Name and timestamp row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 0.5,
              justifyContent: isRightSide ? "flex-end" : "flex-start",
              gap: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              fontWeight={600}
              sx={{
                color: "#374151",
                fontSize: "13px",
              }}
            >
              {getName(msg)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#6b7280",
                fontSize: "11px",
              }}
            >
              {getTime(msg)}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: "#9ca3af",
                fontSize: "10px",
              }}
            >
              {getRelativeTime(msg)}
            </Typography>

            {/* Options menu for edit/delete (only show if message can be modified and not loading) */}
            {canModify && !isMessageEditing && !isMessageDeleting && (
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{
                  opacity: 0.7,
                  transition: "opacity 0.2s",
                  "&:hover": {
                    opacity: 1,
                  },
                  ml: 0.5,
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            )}

            {/* Show loading indicator when message is being processed */}
            {(isMessageEditing || isMessageDeleting) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  ml: 0.5,
                }}
              >
                <CircularProgress size={16} thickness={4} />
                <Typography
                  variant="caption"
                  sx={{
                    ml: 0.5,
                    color: "#6b7280",
                    fontSize: "11px",
                  }}
                >
                  {isMessageEditing ? "Updating..." : "Deleting..."}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Message bubble */}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: messageColors.bgcolor,
              border: `1px solid ${messageColors.borderColor}`,
              borderRadius: "16px",
              borderTopLeftRadius: isRightSide ? "16px" : "4px",
              borderTopRightRadius: isRightSide ? "4px" : "16px",
              position: "relative",
              cursor: shouldTruncate ? "pointer" : "default",
              transition: "all 0.2s ease",
              opacity: isMessageDeleting ? 0.5 : 1,
              pointerEvents: isMessageDeleting ? "none" : "auto",
              "&:hover": shouldTruncate
                ? {
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                    transform: "translateY(-1px)",
                  }
                : {},
            }}
            role={shouldTruncate ? "button" : undefined}
            tabIndex={shouldTruncate ? 0 : undefined}
            aria-expanded={shouldTruncate ? isExpanded : undefined}
            onClick={shouldTruncate ? () => onExpand(idx) : undefined}
            onKeyDown={
              shouldTruncate
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") onExpand(idx);
                  }
                : undefined
            }
          >
            {/* Display attachment if exists */}
            {msg.attachment && (
              <Box sx={{ mb: content ? 1.5 : 0 }}>
                <ImageAttachment
                  url={msg.attachment}
                  isRightSide={isRightSide}
                />
              </Box>
            )}

            {/* Display message text or edit field */}
            {content && (
              <>
                {isEditing ? (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      variant="outlined"
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: "rgba(255, 255, 255, 0.8)",
                          fontSize: "14px",
                        },
                      }}
                      autoFocus
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <Button
                        size="small"
                        onClick={handleEditCancel}
                        startIcon={<Close />}
                        disabled={isMessageEditing}
                        sx={{
                          minWidth: "auto",
                          px: 1.5,
                          py: 0.5,
                          fontSize: "12px",
                        }}
                      >
                        Cancel
                      </Button>
                      <LoadingButton
                        size="small"
                        variant="contained"
                        onClick={handleEditSave}
                        startIcon={<Save />}
                        loading={isMessageEditing}
                        disabled={
                          !editedContent.trim() || editedContent === content
                        }
                        sx={{
                          minWidth: "auto",
                          px: 1.5,
                          py: 0.5,
                          fontSize: "12px",
                        }}
                      >
                        Save
                      </LoadingButton>
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#1f2937",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      lineHeight: 1.4,
                      fontSize: "14px",
                      textAlign: isRightSide ? "right" : "left",
                    }}
                  >
                    {shouldTruncate && !isExpanded
                      ? `${content.substring(0, 200)}...`
                      : content}
                  </Typography>
                )}
              </>
            )}

            {/* Expand/collapse button positioned at bottom right of bubble */}
            {shouldTruncate && !isEditing && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: isRightSide ? "flex-start" : "flex-end",
                  mt: 1,
                  gap: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6366f1",
                    fontSize: "12px",
                    fontWeight: 500,
                  }}
                >
                  {isExpanded ? "Show Less" : "Show More"}
                </Typography>
                <IconButton
                  size="small"
                  sx={{
                    p: 0.25,
                    color: "#6366f1",
                  }}
                >
                  {isExpanded ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Context Menu for Edit/Delete */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        TransitionComponent={Fade}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: 2,
            minWidth: 150,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <MenuItem onClick={handleEditStart} sx={{ py: 1.5 }}>
          <ListItemIcon>
            <Edit fontSize="small" sx={{ color: "#3b82f6" }} />
          </ListItemIcon>
          <ListItemText
            primary="Edit"
            primaryTypographyProps={{ fontSize: "14px" }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => setDeleteDialogOpen(true)}
          sx={{ py: 1.5, color: "#dc2626" }}
        >
          <ListItemIcon>
            <DeleteOutline fontSize="small" sx={{ color: "#dc2626" }} />
          </ListItemIcon>
          <ListItemText
            primary="Delete"
            primaryTypographyProps={{ fontSize: "14px", color: "#dc2626" }}
          />
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Delete sx={{ color: "#dc2626" }} />
            <Typography variant="h6" component="span">
              Delete Message
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this message? This action cannot be
            undone.
          </Typography>
          {content && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#f9fafb",
                borderRadius: 2,
                border: "1px solid #e5e7eb",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontStyle: "italic",
                  color: "#6b7280",
                  fontSize: "13px",
                  maxHeight: "60px",
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                }}
              >
                "{content}"
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            size="small"
            disabled={isMessageDeleting}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            size="small"
            startIcon={<Delete />}
            loading={isMessageDeleting}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessageContainer;
